import { supabase } from "../utils/supabaseClient";

export type UserProfile = {
  id: string;
  name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  role: "initial" | "user" | "disapproved" | "admin";
  created_at: string;
  updated_at: string;
};

export type UserManagementFilters = {
  role?: "initial" | "user" | "disapproved" | "admin";
  search?: string;
};

export type PaginationParams = {
  page?: number;
  limit?: number;
};

export type UserManagementResponse = {
  data: UserProfile[];
  count: number;
  error: string | null;
};

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001";

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class UserManagementService {
  static async getUsers(
    filters: UserManagementFilters = {},
    pagination: PaginationParams = {}
  ): Promise<UserManagementResponse> {
    try {
      const { page = 1, limit = 10 } = pagination;
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (filters.role) params.set("role", filters.role);
      if (filters.search) params.set("search", filters.search);

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users?${params}`, { headers });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to fetch users");

      return { data: json.data || [], count: json.count || 0, error: null };
    } catch (error) {
      console.error("Error fetching users:", error);
      return {
        data: [],
        count: 0,
        error: error instanceof Error ? error.message : "Failed to fetch users",
      };
    }
  }

  static async getUserStats(): Promise<{
    data: { total: number; initial: number; approved: number; disapproved: number; admin: number } | null;
    error: string | null;
  }> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users/stats`, { headers });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to fetch user stats");

      return { data: json.data, error: null };
    } catch (error) {
      console.error("Error fetching user stats:", error);
      return {
        data: null,
        error: error instanceof Error ? error.message : "Failed to fetch user statistics",
      };
    }
  }

  static async approveUser(userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/approve`, {
        method: "POST",
        headers,
      });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to approve user");

      return { success: true, error: null };
    } catch (error) {
      console.error("Error approving user:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to approve user",
      };
    }
  }

  static async disapproveUser(userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/disapprove`, {
        method: "POST",
        headers,
      });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to disapprove user");

      return { success: true, error: null };
    } catch (error) {
      console.error("Error disapproving user:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to disapprove user",
      };
    }
  }

  static async bulkApproveUsers(userIds: string[]): Promise<{ success: boolean; error: string | null }> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users/bulk-approve`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ userIds }),
      });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to bulk approve users");

      return { success: true, error: null };
    } catch (error) {
      console.error("Error bulk approving users:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to approve users",
      };
    }
  }

  static async getPendingUsersCount(): Promise<{ count: number; error: string | null }> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/admin/users/stats`, { headers });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to fetch pending users count");

      return { count: json.data?.initial || 0, error: null };
    } catch (error) {
      console.error("Error fetching pending users count:", error);
      return {
        count: 0,
        error: error instanceof Error ? error.message : "Failed to fetch pending users count",
      };
    }
  }

  static isSignupBlockBypassed(): boolean {
    return import.meta.env.VITE_BYPASS_SIGNUP_BLOCK === "true";
  }
}
